import type { Address, AddressWithGeography } from "@asap/shared/utils/supabase.types";
import type { LocationFilter } from "@asap/shared/schemas/list";
import type { Department } from "@asap/shared/schemas/address";

export const formatAddress = (...address: (string | null | undefined)[]) => address.filter(Boolean).join(", ");

export const toAddressPrefill = (value: string | undefined): LocationFilter | undefined =>
  value ? { latitude: 0, longitude: 0, radius: 0, type: "street", label: value } : undefined;

// cf https://fr.wikipedia.org/wiki/France_d%27outre-mer
const overseasFranceZipCodes = [
  /**
   * DOM
   */
  "971", // Guadeloupe
  "972", // Martinique
  "973", // Guyane
  "974", // La Réunion
  "975", // Saint-Pierre-et-Miquelon
  "976", // Mayotte
  "977", // Saint-Barthélemy
  "978", // Saint-Martin

  /**
   * TOM
   */
  "984", // Terres australes et antarctiques françaises
  "986", // Wallis-et-Futuna
  "987", // Polynésie française
  "988", // Nouvelle-Calédonie
  "989", // Île de Clipperton
];

export const extractZipCode = (address: { postal_code: string | null }) => {
  for (const code of overseasFranceZipCodes) {
    if (address.postal_code?.startsWith(code)) return code;
  }
  return address.postal_code?.slice(0, 2);
};

export const simplifyAddress = function (address: {
  city: string | null;
  region: string | null;
  postal_code: string | null;
}) {
  let result = "";

  if (address.city) result = address.city;
  else if (address.region) result = address.region;

  if (extractZipCode(address)) result = `${result} (${extractZipCode(address)})`;

  return result;
};

export const addressDetailsLine1 = (address: Address) => {
  return `${address.street1 ? address.street1 + "," : ""} ${address.street2}`;
};

export const addressDetailsLine2 = (address: Address) => {
  return `${address.city} ${address.postal_code}`;
};

export const addressDetailsLine3 = (address: Address) => {
  return `${address.department ? address.department + "," : ""} ${address.country}`;
};

export const isAddressWithGeography = function (address: Address): address is AddressWithGeography {
  return !!address.coordinates && address.coordinates.lat !== null && address.coordinates.lng !== null;
};

export const formatAddressForTable = (department: Department | null) => {
  if (!department) return "";
  return formatAddress(department.local_code, department.name);
};

export const formatAddressTooltipForTable = (department: Department | null) => {
  if (!department) return "";
  return [formatAddressForTable(department), department.region?.name].filter(Boolean).join(" — ");
};
